import { Controller } from "@hotwired/stimulus";

const PLAN_CONFIG = {
  pro: {
    borderSelected: "border-purple-500",
    bgSelected: "bg-purple-50",
    textColor: "text-purple-600",
    focusRing: "focus:ring-purple-500",
    focusBorder: "focus:border-purple-500",
    gradient: "from-purple-600 to-blue-600",
    hoverClasses: ["hover:from-purple-700", "hover:to-blue-700"],
    submitRing: "focus:ring-purple-500",
    highlightClasses: "bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200",
  },
  team: {
    borderSelected: "border-teal-500",
    bgSelected: "bg-teal-50",
    textColor: "text-teal-600",
    focusRing: "focus:ring-teal-500",
    focusBorder: "focus:border-teal-500",
    gradient: "from-teal-600 to-blue-600",
    hoverClasses: ["hover:from-teal-700", "hover:to-blue-700"],
    submitRing: "focus:ring-teal-500",
    highlightClasses: "bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200",
  },
  business: {
    borderSelected: "border-blue-500",
    bgSelected: "bg-blue-50",
    textColor: "text-blue-600",
    focusRing: "focus:ring-blue-500",
    focusBorder: "focus:border-blue-500",
    gradient: "from-blue-600 to-purple-600",
    hoverClasses: ["hover:from-blue-700", "hover:to-purple-700"],
    submitRing: "focus:ring-blue-500",
    highlightClasses: "bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200",
  }
};

export default class extends Controller {
  static targets = [
    "planInput",
    "planCard",
    "featuresSection",
    "featuresTitle",
    "featuresList",
    "pricingHighlight",
    "submitButton",
  ];

  connect() {
    this.updateUI();
  }

  selectPlan(event) {
    const selectedPlan = event.currentTarget.dataset.plan;
    this.planInputTarget.value = selectedPlan;

    const url = new URL(window.location);
    url.searchParams.set("plan", selectedPlan);
    window.history.replaceState({}, "", url);

    this.updateUI();
  }

  updateUI() {
    const selectedPlan = this.planInputTarget.value;
    const config = PLAN_CONFIG[selectedPlan] || PLAN_CONFIG.pro;

    this.planCardTargets.forEach((card) => {
      const planType = card.dataset.plan;
      const isSelected = planType === selectedPlan;
      const cardConfig = PLAN_CONFIG[planType] || PLAN_CONFIG.pro;

      card.classList.remove(
        "border-purple-500", "bg-purple-50",
        "border-teal-500", "bg-teal-50",
        "border-blue-500", "bg-blue-50",
        "border-gray-200"
      );

      if (isSelected) {
        card.classList.add(cardConfig.borderSelected, cardConfig.bgSelected);
      } else {
        card.classList.add("border-gray-200");
      }
    });

    this.updateFeatures(selectedPlan);

    const pricingHighlight = this.pricingHighlightTarget;
    const planNameEl = pricingHighlight.querySelector("p:first-child");
    const priceAmount = pricingHighlight.querySelector(".text-lg");
    const section = this.featuresSectionTarget;
    const planDisplayName = section.dataset[`planNames${this.capitalize(selectedPlan)}`] || this.capitalize(selectedPlan);
    const selectedCard = this.planCardTargets.find((c) => c.dataset.plan === selectedPlan);
    const intervalInput = document.querySelector('input[name="interval"]');
    const interval = intervalInput?.value || "month";

    let priceLabel;
    if (interval === "year" && selectedCard) {
      const yearlyEquiv = selectedCard.dataset.yearlyEquiv;
      priceLabel = yearlyEquiv ? `$${yearlyEquiv}/mo billed yearly` : "";
    } else {
      const monthlyPrice = selectedCard?.dataset.monthlyPrice;
      priceLabel = monthlyPrice ? `$${monthlyPrice}/month` : "";
    }
    planNameEl.textContent = `${planDisplayName} Plan${priceLabel ? ` - ${priceLabel}` : ""}`;
    priceAmount.textContent = "$0 today";
    priceAmount.className = `text-lg font-bold ${config.textColor}`;
    pricingHighlight.className = `${config.highlightClasses} rounded-lg p-4`;

    const submitButton = this.submitButtonTarget;
    submitButton.textContent = `Start ${planDisplayName} Trial`;
    submitButton.className =
      `w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r ${config.gradient} ${config.hoverClasses.join(" ")} focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.submitRing} transition-all duration-200`;

    const inputs = document.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="password"]'
    );
    inputs.forEach((input) => {
      input.classList.remove(
        "focus:ring-purple-500", "focus:border-purple-500",
        "focus:ring-teal-500", "focus:border-teal-500",
        "focus:ring-blue-500", "focus:border-blue-500"
      );
      input.classList.add(config.focusRing, config.focusBorder);
    });
  }

  updateFeatures(plan) {
    const section = this.featuresSectionTarget;
    const featuresJson = section.dataset[`features${this.capitalize(plan)}`];
    if (!featuresJson) return;

    const features = JSON.parse(featuresJson);
    const planName = section.dataset[`planNames${this.capitalize(plan)}`] || this.capitalize(plan);

    this.featuresTitleTarget.textContent = `${planName} Features Included`;

    const list = this.featuresListTarget;
    list.innerHTML = features
      .map(
        (f) =>
          `<li class="flex items-center"><svg class="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"></path></svg><span class="text-sm text-gray-700">${f}</span></li>`
      )
      .join("");
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
